// web/src/features/home/WidgetCard.tsx
import type { ReactNode } from 'react';
import { DropdownMenu } from '@/components/ui/DropdownMenu';
import type { WidgetDef } from './registry';
import { CARD_FRAME, COMPACT_FRAME, CARD_TITLE } from './homeStyles';

export function WidgetCard({ def, title, headerAction, menuItems, headerRight, children, className = '', style, bare = false, compact = false, onHeaderPointerDown }: {
  def: WidgetDef;
  // Overrides the header text (icon stays def.icon). Falls back to def.label.
  title?: ReactNode;
  // Rendered on the header row, right of the title (before drag/menu).
  headerAction?: ReactNode;
  menuItems?: { label: string; onClick: () => void }[];
  headerRight?: ReactNode;
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  // Hide the header (icon + title + menu). Used in view mode — titles only show
  // while customizing. The card border/body remain.
  bare?: boolean;
  // Tighten header + body padding for short fixed tiles (e.g. KPI) so their
  // content fits without the tile needing extra height.
  compact?: boolean;
  // When set (customize mode), the WHOLE header becomes the drag grab area — a
  // large, obvious target rather than a tiny grip. The menu opts out via
  // stopPropagation so clicking it never starts a drag.
  onHeaderPointerDown?: (e: React.PointerEvent) => void;
}) {
  // View (bare) clips content to the rounded card. Customize (has header/menu)
  // must let the ⋮ dropdown escape a short tile, so the section is overflow-visible
  // and the body clips its own rounded bottom instead.
  return (
    <section style={style} className={`min-h-0 flex flex-col ${bare ? 'overflow-hidden' : 'overflow-visible'} ${compact ? COMPACT_FRAME : CARD_FRAME} ${className}`}>
      {!bare && (
        <div onPointerDown={onHeaderPointerDown}
          className={`flex items-center gap-2 border-b border-border-light ${compact ? 'px-2.5 py-0.5' : 'px-3 py-1.5'} ${onHeaderPointerDown ? 'cursor-grab active:cursor-grabbing select-none touch-none' : ''}`}>
          <span className="w-5 h-5 rounded-md inline-flex items-center justify-center text-white shrink-0 [&_svg]:w-3 [&_svg]:h-3" style={{ background: def.accent }}>{def.icon}</span>
          <h2 className={`${CARD_TITLE} truncate flex-1 min-w-0`}
            title={typeof (title ?? def.label) === 'string' ? (title ?? def.label) as string : undefined}>{title ?? def.label}</h2>
          {headerAction && <span className="shrink-0" onPointerDown={(e) => e.stopPropagation()}>{headerAction}</span>}
          {headerRight}
          {menuItems && menuItems.length > 0 && (
            <span onPointerDown={(e) => e.stopPropagation()}>
              <DropdownMenu align="right" items={menuItems}
                label={`${typeof title === 'string' && title ? title : def.label} options`}
                trigger={<div className="text-muted hover:text-secondary cursor-pointer px-1 select-none" title="Options">&#8942;</div>} />
            </span>
          )}
        </div>
      )}
      <div className={`scroll-slim flex-1 min-h-0 overflow-auto ${compact ? 'p-2' : 'p-4'} ${bare ? '' : 'rounded-b-[10px]'}`}>{children}</div>
    </section>
  );
}
